/**
 * Sticky Add to Cart Bar — PATCHED
 *
 * Original behaviour: the sticky "Configure" button only SCROLLED the page up
 * to the main product form. It never added anything to the cart.
 *
 * This patch makes the sticky button behave as a real ATC:
 *   • Click triggers the main product form's submit (full Shopify cart pipeline,
 *     including Dawn's <product-form> custom element + cart drawer refresh).
 *   • Sticky button text mirrors the main ATC ("Add to cart" / "Added" / "Sold out").
 *   • Sticky button mirrors disabled state, loading spinner, and success state.
 *   • Caret icon is hidden (it implied "tap to expand", which no longer applies).
 *
 * Also fixes a class-name bug:
 *   • JS used to add `sticky-addtocart--visible`, but the CSS in
 *     section-main-product.css listens for `.active`. The bar's scroll-triggered
 *     show/hide was therefore dead. Now uses `.active` to match the CSS.
 *
 * No Liquid or CSS changes required. Just replace this file.
 */

class StickyAddToCart {
  constructor() {
    this.stickyWrapper = document.querySelector('.sticky-addtocart-wrapper');
    this.stickyButton = document.querySelector('[data-sticky-atc-button]');

    if (!this.stickyWrapper || !this.stickyButton) return;

    // Guard against double-init from shopify:section:load
    if (this.stickyButton.dataset.stickyInit === 'true') return;
    this.stickyButton.dataset.stickyInit = 'true';

    this.stickyTextEl = this.stickyButton.querySelector('.button__text');
    this.stickyCaretEl = this.stickyButton.querySelector('.button__caret');
    this.stickySpinner = this.stickyButton.querySelector('.loading-overlay__spinner');

    this.mainSubmitButton = document.querySelector(
      '.product-form__submit:not(.sticky-product-form__submit)'
    );
    this.mainProductForm = document.querySelector('product-form');
    this.backToTopButton = document.getElementById('back-to-top');

    this.init();
  }

  init() {
    this.setupStickyButtonClick();
    this.setupMainButtonMirror();
    this.setupBackToTopVisibility();
    this.setupStickyBarVisibility();
    this.hideCaret();
    this.syncFromMain(); // initial state
  }

  /**
   * Caret implied "tap to expand/scroll" — no longer accurate.
   */
  hideCaret() {
    if (this.stickyCaretEl) this.stickyCaretEl.style.display = 'none';
  }

  /**
   * Main click behaviour: trigger main ATC button so the full form submission
   * pipeline runs (variant validation, AJAX add, cart drawer refresh, etc.).
   * Falls back to scroll-to-form only if the main button can't be found.
   */
  setupStickyButtonClick() {
    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (this.mainSubmitButton && !this.mainSubmitButton.disabled) {
        // Programmatically click the main ATC. This dispatches the form's
        // submit event, which Dawn's <product-form> handler intercepts and
        // routes through /cart/add.js + cart drawer refresh.
        this.mainSubmitButton.click();
        return;
      }

      // Fallback: main button missing or disabled → scroll to form
      this.scrollToForm();
    });
  }

  scrollToForm() {
    const target =
      document.querySelector('.product-form__buttons') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('.product-form__submit');
    if (!target) return;

    const header = document.querySelector(
      '.section-header, .shopify-section-header, header'
    );
    const stickyBarHeight = this.stickyWrapper.offsetHeight || 0;
    const headerHeight = header ? header.offsetHeight : 0;
    const stickyPosition = this.stickyWrapper.dataset.stickyPosition;
    const offset =
      stickyPosition === 'top'
        ? headerHeight + stickyBarHeight + 20
        : headerHeight + 20;
    const targetPosition =
      target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    setTimeout(() => {
      if (this.mainSubmitButton) this.mainSubmitButton.focus();
    }, 500);
  }

  /**
   * Watch the main ATC for text/disabled/class changes and mirror them
   * onto the sticky button so the two stay in lockstep visually.
   */
  setupMainButtonMirror() {
    if (!this.mainSubmitButton) return;

    const observer = new MutationObserver(() => this.syncFromMain());
    observer.observe(this.mainSubmitButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      subtree: true,
      childList: true,
      characterData: true
    });

    // Also re-sync after variant picker interactions
    if (this.mainProductForm) {
      this.mainProductForm.addEventListener('change', () => {
        setTimeout(() => this.syncFromMain(), 50);
      });
    }
  }

  syncFromMain() {
    if (!this.mainSubmitButton) return;

    const mainTextEl = this.mainSubmitButton.querySelector('.button__text');
    const mainDisabled =
      this.mainSubmitButton.disabled ||
      this.mainSubmitButton.hasAttribute('disabled');
    const mainLoading = this.mainSubmitButton.classList.contains('loading');
    const mainAdded = this.mainSubmitButton.classList.contains('added');

    // Mirror disabled (but not while loading — disabled-during-loading is OK
    // because the click should be ignored anyway)
    this.stickyButton.disabled = mainDisabled && !mainLoading;

    // Mirror loading
    this.stickyButton.classList.toggle('loading', mainLoading);
    if (this.stickySpinner) {
      this.stickySpinner.classList.toggle('hidden', !mainLoading);
    }

    // Mirror added (success) state
    this.stickyButton.classList.toggle('added', mainAdded);

    // Mirror text content
    if (this.stickyTextEl && mainTextEl) {
      const newText = mainTextEl.textContent.trim();
      if (this.stickyTextEl.textContent.trim() !== newText) {
        this.stickyTextEl.textContent = newText;
      }
    }
  }

  setupBackToTopVisibility() {
    if (!this.backToTopButton) return;
    this.backToTopButton.classList.add('back-to-top--hidden-by-sticky');
    this.backToTopButton.style.display = 'none';
  }

  /**
   * Show the bar when the main ATC has scrolled out of view, hide it near
   * the footer. Uses `.active` to match the CSS in section-main-product.css.
   */
  setupStickyBarVisibility() {
    const buyButtons =
      document.querySelector('.product-form__buttons') ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    if (!buyButtons) return;

    const footer =
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer');
    let ticking = false;

    const updateVisibility = () => {
      const buyButtonsRect = buyButtons.getBoundingClientRect();
      let shouldShow = buyButtonsRect.bottom < 0;

      if (shouldShow && footer) {
        const footerRect = footer.getBoundingClientRect();
        const footerVisibleHeight = window.innerHeight - footerRect.top;
        const seventyPercent = footer.offsetHeight * 0.7;
        if (footerVisibleHeight >= seventyPercent) shouldShow = false;
      }

      // FIX: use `active` (matches CSS), not the non-existent `sticky-addtocart--visible`
      this.stickyWrapper.classList.toggle('active', shouldShow);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateVisibility);
          ticking = true;
        }
      },
      { passive: true }
    );

    updateVisibility();
  }
}

/**
 * Add to Cart Button States
 * Handles loading and success states for the MAIN add-to-cart button.
 * (Unchanged from original — sticky mirrors these states automatically
 * via the MutationObserver above.)
 */
class AddToCartButtonStates {
  constructor() {
    this.successDuration = 2000;
    this.init();
  }

  init() {
    if (customElements.get('product-form')) {
      this.setupAllForms();
    } else {
      customElements.whenDefined('product-form').then(() => {
        this.setupAllForms();
      });
    }
  }

  setupAllForms() {
    document.querySelectorAll('product-form').forEach((form) => {
      this.setupForm(form);
    });
  }

  setupForm(productForm) {
    const submitButton = productForm.querySelector('[name="add"]');
    if (!submitButton) return;

    const textSpan = this.getButtonTextElement(submitButton);
    if (textSpan && !submitButton.dataset.originalText) {
      submitButton.dataset.originalText = textSpan.textContent.trim();
    }

    this.interceptFormSubmit(productForm, submitButton);
  }

  getButtonTextElement(button) {
    const buttonText = button.querySelector('.button__text');
    if (buttonText) return buttonText;

    const directSpan = button.querySelector(
      ':scope > span:not(.loading-overlay__spinner)'
    );
    if (directSpan) {
      const nestedText = directSpan.querySelector('.button__text');
      if (nestedText) return nestedText;

      if (!directSpan.querySelector('.button__arrow')) {
        return directSpan;
      }

      const textContainer = directSpan.childNodes[0];
      if (textContainer && textContainer.nodeType === Node.TEXT_NODE) {
        const wrapper = document.createElement('span');
        wrapper.className = 'button__text';
        wrapper.textContent = textContainer.textContent.trim();
        directSpan.replaceChild(wrapper, textContainer);
        return wrapper;
      }
    }

    return null;
  }

  interceptFormSubmit(productForm, submitButton) {
    if (productForm.dataset.atcStatesBound === 'true') return;
    productForm.dataset.atcStatesBound = 'true';

    const originalOnSubmitHandler = productForm.onSubmitHandler.bind(productForm);

    productForm.onSubmitHandler = async (evt) => {
      evt.preventDefault();

      this.showLoadingState(submitButton);

      try {
        await originalOnSubmitHandler(evt);
        this.showSuccessState(submitButton);
      } catch (error) {
        this.resetButton(submitButton);
        console.error('Add to cart error:', error);
      }
    };
  }

  showLoadingState(button) {
    button.classList.add('loading');
    button.classList.remove('added');
    button.disabled = true;

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.remove('hidden');

    button.setAttribute('aria-label', 'Adding to cart');
  }

  showSuccessState(button) {
    button.classList.remove('loading');

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.add('hidden');

    setTimeout(() => {
      const textSpan = this.getButtonTextElement(button);
      if (textSpan) {
        textSpan.textContent = window.variantStrings?.added || 'Added';
      }

      button.classList.add('added');
      button.disabled = false;
      button.setAttribute('aria-label', 'Added to cart');

      setTimeout(() => {
        this.resetButton(button);
      }, this.successDuration);
    }, 300);
  }

  resetButton(button) {
    button.classList.remove('added', 'loading');
    button.disabled = false;

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.add('hidden');

    const textSpan = this.getButtonTextElement(button);
    if (textSpan && button.dataset.originalText) {
      textSpan.textContent = button.dataset.originalText;
    }

    button.removeAttribute('aria-label');
  }
}

function initStickyAddToCart() {
  new StickyAddToCart();
  new AddToCartButtonStates();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyAddToCart);
} else {
  initStickyAddToCart();
}

document.addEventListener('shopify:section:load', (event) => {
  if (
    event.target.querySelector('product-form') ||
    event.target.querySelector('.sticky-addtocart-wrapper')
  ) {
    initStickyAddToCart();
  }
});
